import React, { useEffect, useState } from 'react';
import {
	Autocomplete,
	Button,
	IconButton,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Catalog, getCatalog, Requirement } from '../../Services/agents';

interface Props {
	requirements: Requirement[];
	onChange: (requirements: Requirement[]) => void;
}

// #region useCatalog
// Le catalogue des connecteurs, chargé une fois : un SKU tapé à la main avec une faute
// ferait un prérequis jamais rempli, sans aucune erreur.
const useCatalog = (): Catalog | null => {
	const [catalog, setCatalog] = useState<Catalog | null>(null);
	useEffect(() => {
		getCatalog()
			.then(setCatalog)
			.catch(() => setCatalog({ connectors: [], families: [] }));
	}, []);
	return catalog;
};
// #endregion

// #region RequirementsEditor
// Les données attendues par un agent. Elles ne bloquent rien : elles orientent le
// planificateur (qui dit au client ce qui manque et quoi brancher) et s'affichent au
// client, cochées. Essentielle / utile = l'insistance. Valeurs d'une ligne = « l'un de ».
export const RequirementsEditor: React.FC<Props> = ({
	requirements,
	onChange,
}) => {
	const catalog = useCatalog();
	const set = (i: number, r: Requirement) =>
		onChange(requirements.map((x, j) => (j === i ? r : x)));
	const add = () =>
		onChange([
			...requirements,
			{ kind: 'connector', values: [], level: 'recommended', why: '' },
		]);

	return (
		<Stack spacing={1}>
			{requirements.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					Aucune donnée attendue : l'agent travaille avec ce que le projet a.
				</Typography>
			)}
			{requirements.map((r, i) => (
				<RequirementRow
					key={i}
					req={r}
					catalog={catalog}
					onChange={(nr) => set(i, nr)}
					onRemove={() => onChange(requirements.filter((_, j) => j !== i))}
				/>
			))}
			<Button
				startIcon={<AddIcon />}
				onClick={add}
				sx={{ alignSelf: 'flex-start' }}
			>
				Ajouter une donnée attendue
			</Button>
		</Stack>
	);
};
// #endregion

// #region RequirementRow
const RequirementRow: React.FC<{
	req: Requirement;
	catalog: Catalog | null;
	onChange: (r: Requirement) => void;
	onRemove: () => void;
}> = ({ req, catalog, onChange, onRemove }) => {
	const setLevel = (level: Requirement['level']) => onChange({ ...req, level });
	const options =
		req.kind === 'family'
			? (catalog?.families || []).map((f) => ({
					value: f.value,
					label: f.label,
				}))
			: (catalog?.connectors || []).map((c) => ({
					value: c.sku,
					label: `${c.name} (${c.sku}) · ${c.family}`,
				}));
	const selected = req.values.map(
		(v) => options.find((o) => o.value === v) || { value: v, label: v }
	);

	return (
		<Paper variant="outlined" sx={{ p: 1.5 }}>
			<Stack
				direction="row"
				spacing={1}
				alignItems="flex-start"
				sx={{ flexWrap: 'wrap', rowGap: 1 }}
			>
				<TextField
					select
					size="small"
					label="Donnée"
					value={req.level}
					onChange={(e) => setLevel(e.target.value as Requirement['level'])}
					sx={{ width: 140 }}
				>
					<MenuItem value="required">Essentielle</MenuItem>
					<MenuItem value="recommended">Utile</MenuItem>
				</TextField>
				<TextField
					select
					size="small"
					label="Type"
					value={req.kind}
					onChange={(e) =>
						onChange({
							...req,
							kind: e.target.value as Requirement['kind'],
							values: [],
						})
					}
					sx={{ width: 140 }}
				>
					<MenuItem value="family">Famille</MenuItem>
					<MenuItem value="connector">Connecteurs</MenuItem>
				</TextField>
				<Autocomplete
					multiple
					size="small"
					options={options}
					value={selected}
					loading={!catalog}
					isOptionEqualToValue={(a, b) => a.value === b.value}
					getOptionLabel={(o) => o.label}
					onChange={(_, v) =>
						onChange({ ...req, values: v.map((o) => o.value) })
					}
					renderInput={(p) => <TextField {...p} label="L'un de" />}
					sx={{ flex: 1, minWidth: 260 }}
				/>
				<Tooltip title="Retirer">
					<IconButton size="small" onClick={onRemove}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>
			<TextField
				size="small"
				fullWidth
				label="Pourquoi (affiché au client, en anglais)"
				value={req.why}
				onChange={(e) => onChange({ ...req, why: e.target.value })}
				inputProps={{ maxLength: 200 }}
				sx={{ mt: 1 }}
			/>
		</Paper>
	);
};
// #endregion
